import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Gelir {
  id: number;
  tarih: string;
  aciklama: string;
  detay: string;
  kopek: string;
  tutar: number;
  created_at: string;
}

interface GelirContextType {
  gelirler: Gelir[];
  addGelir: (gelir: Omit<Gelir, 'id'>) => void;
  updateGelir: (id: number, gelir: Omit<Gelir, 'id'>) => void;
  deleteGelir: (id: number) => void;
  getToplamGelir: () => number;
  getBuAykiGelir: () => number;
  getAylikGelir: (ay: number, yil: number) => number;
  getAylikGelirData: () => { ay: string; gelir: number }[];
  getHaftalikGelirData: () => { hafta: string; gelir: number }[];
  getYillikGelirData: () => { yil: string; gelir: number }[];
  getGelirDagilimi: () => { isim: string; deger: number; tutar: number }[];
}

const GelirContext = createContext<GelirContextType | undefined>(undefined);

export const useGelir = () => {
  const context = useContext(GelirContext);
  if (!context) {
    throw new Error('useGelir must be used within a GelirProvider');
  }
  return context;
};

interface GelirProviderProps {
  children: ReactNode;
}

export const GelirProvider: React.FC<GelirProviderProps> = ({ children }) => {
  const [gelirler, setGelirler] = useState<Gelir[]>([]);

  const addGelir = (yeniGelir: Omit<Gelir, 'id'>) => {
    const id = Date.now(); // Basit ID üretimi
    const gelir: Gelir = {
      ...yeniGelir,
      id
    };
    setGelirler(prev => [...prev, gelir]);
  };

  const updateGelir = (id: number, guncelGelir: Omit<Gelir, 'id'>) => {
    setGelirler(prev => prev.map(gelir => 
      gelir.id === id ? { ...guncelGelir, id } : gelir
    ));
  };

  const deleteGelir = (id: number) => {
    setGelirler(prev => prev.filter(gelir => gelir.id !== id));
  };

  const getToplamGelir = () => {
    return gelirler.reduce((total, gelir) => total + gelir.tutar, 0);
  };

  const getBuAykiGelir = () => {
    const bugun = new Date();
    const buAy = bugun.getMonth() + 1; // getMonth() 0-11 arası döner
    const buYil = bugun.getFullYear();
    return getAylikGelir(buAy, buYil);
  };

  const getAylikGelir = (ay: number, yil: number) => {
    return gelirler
      .filter(gelir => {
        const tarih = new Date(gelir.tarih);
        return tarih.getMonth() === ay - 1 && tarih.getFullYear() === yil;
      })
      .reduce((total, gelir) => total + gelir.tutar, 0);
  };

  const getAylikGelirData = () => {
    const aylar = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];

    const currentYear = new Date().getFullYear();
    
    return aylar.map((ay, index) => ({
      ay,
      gelir: getAylikGelir(index + 1, currentYear)
    }));
  };

  const getHaftalikGelirData = () => {
    const bugün = new Date();
    const haftalikData: { hafta: string; gelir: number }[] = [];
    
    // Son 8 haftanın verilerini al
    for (let i = 7; i >= 0; i--) {
      const haftaBaslangic = new Date(bugün);
      haftaBaslangic.setDate(bugün.getDate() - (i * 7) - bugün.getDay());
      haftaBaslangic.setHours(0, 0, 0, 0);
      
      const haftaBitis = new Date(haftaBaslangic);
      haftaBitis.setDate(haftaBaslangic.getDate() + 6);
      haftaBitis.setHours(23, 59, 59, 999);
      
      const haftaGeliri = gelirler
        .filter(gelir => {
          const gelirTarihi = new Date(gelir.tarih);
          return gelirTarihi >= haftaBaslangic && gelirTarihi <= haftaBitis;
        })
        .reduce((total, gelir) => total + gelir.tutar, 0);
      
      const haftaAdi = `${haftaBaslangic.getDate()}/${haftaBaslangic.getMonth() + 1} - ${haftaBitis.getDate()}/${haftaBitis.getMonth() + 1}`;
      
      haftalikData.push({
        hafta: haftaAdi,
        gelir: haftaGeliri
      });
    }
    
    return haftalikData;
  };

  const getYillikGelirData = () => {
    const currentYear = new Date().getFullYear();
    const yillikData: { yil: string; gelir: number }[] = [];
    
    // Son 5 yılın verilerini al
    for (let i = 4; i >= 0; i--) {
      const yil = currentYear - i;
      const yilGeliri = gelirler
        .filter(gelir => {
          const gelirTarihi = new Date(gelir.tarih);
          return gelirTarihi.getFullYear() === yil;
        })
        .reduce((total, gelir) => total + gelir.tutar, 0);
      
      yillikData.push({
        yil: yil.toString(),
        gelir: yilGeliri
      });
    }
    
    return yillikData;
  };

  const getGelirDagilimi = () => {
    const dagitim: { [key: string]: number } = {};
    let toplam = 0;

    // Gelir türlerini hesapla
    gelirler.forEach(gelir => {
      dagitim[gelir.aciklama] = (dagitim[gelir.aciklama] || 0) + gelir.tutar;
      toplam += gelir.tutar;
    });

    // Yüzdelik dilimler hesapla
    return Object.entries(dagitim).map(([isim, tutar]) => ({
      isim,
      tutar,
      deger: toplam > 0 ? Math.round((tutar / toplam) * 100) : 0
    })).sort((a, b) => b.tutar - a.tutar);
  };

  const value: GelirContextType = {
    gelirler,
    addGelir,
    updateGelir,
    deleteGelir,
    getToplamGelir,
    getBuAykiGelir,
    getAylikGelir,
    getAylikGelirData,
    getHaftalikGelirData,
    getYillikGelirData,
    getGelirDagilimi
  };

  return (
    <GelirContext.Provider value={value}>
      {children}
    </GelirContext.Provider>
  );
};
